/*
  Samuel Phillip & Co. private virtual showing tracking.

  dataLayer events:
  - private_showing_button_click: diagnostic only. Do not use as the final conversion.
  - private_showing_calendar_opened: diagnostic only. Do not use as the final conversion.
  - private_virtual_showing_booked: actual GA4 / Google Ads conversion event.

  The Cowlendar booking widget can render inside a cross-origin iframe. The theme cannot
  safely inspect a cross-origin iframe for a completed booking, so the final conversion is
  only pushed when an explicit success-style postMessage is available. If Cowlendar does
  not expose one, use a post-booking redirect, a booking-created webhook, or an official
  frontend success callback from app support.
*/

(function () {
  'use strict';

  var TRACKING_SELECTOR = '[data-spc-tracking="private-virtual-showing"], [data-cowlendar-btn]';
  var BOOKING_TYPE = 'private_virtual_showing';
  var BOOKED_DEDUPE_PREFIX = 'spc_private_virtual_showing_booked';
  var BOOKED_DEDUPE_TTL = 30 * 60 * 1000;
  var lastContext = null;
  var calendarOpenTracked = false;

  window.dataLayer = window.dataLayer || [];

  function compact(payload) {
    Object.keys(payload).forEach(function (key) {
      if (payload[key] === undefined || payload[key] === null || payload[key] === '') {
        delete payload[key];
      }
    });

    return payload;
  }

  function pushEvent(payload) {
    window.dataLayer.push(compact(payload));
  }

  function getPageType(sourceLocation) {
    if (sourceLocation === 'cart_drawer') {
      return 'cart_drawer';
    }

    if (document.body.classList.contains('template-product')) {
      return 'product';
    }

    if (document.body.classList.contains('template-cart')) {
      return 'cart';
    }

    if (window.location.pathname.replace(/\/$/, '') === '/cart') {
      return 'cart';
    }

    return 'unknown';
  }

  function inferSourceLocation(trigger) {
    if (trigger && trigger.dataset.spcSourceLocation) {
      return trigger.dataset.spcSourceLocation;
    }

    if (trigger && trigger.closest('#Cart-Drawer, .cart-drawer')) {
      return 'cart_drawer';
    }

    if (trigger && trigger.closest('.cart-summary-panel, .cart-section')) {
      return 'cart';
    }

    if (document.body.classList.contains('template-product')) {
      return 'pdp';
    }

    return 'unknown';
  }

  function getCurrentVariantId(trigger) {
    if (trigger && trigger.dataset.variantId) {
      return trigger.dataset.variantId;
    }

    var productFormVariantInput = document.querySelector('form[data-type="add-to-cart-form"] input[name="id"]');

    return productFormVariantInput ? productFormVariantInput.value : undefined;
  }

  function getContext(trigger) {
    var sourceLocation = inferSourceLocation(trigger);

    return {
      booking_type: BOOKING_TYPE,
      source_location: sourceLocation,
      page_type: getPageType(sourceLocation),
      product_id: trigger ? trigger.dataset.productId : undefined,
      product_handle: trigger ? trigger.dataset.productHandle : undefined,
      product_title: trigger ? trigger.dataset.productTitle : undefined,
      variant_id: getCurrentVariantId(trigger),
      page_path: window.location.pathname
    };
  }

  function setLastContext(context) {
    lastContext = context || lastContext || {
      booking_type: BOOKING_TYPE,
      source_location: 'unknown',
      page_type: getPageType('unknown'),
      page_path: window.location.pathname
    };
  }

  function getLastContext() {
    if (!lastContext) {
      setLastContext(null);
    }

    return lastContext;
  }

  function isVisible(element) {
    if (!element || !(element instanceof Element)) {
      return false;
    }

    var style = window.getComputedStyle(element);
    var rect = element.getBoundingClientRect();

    return style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      parseFloat(style.opacity || '1') > 0.01 &&
      rect.width > 20 &&
      rect.height > 20;
  }

  function looksLikeCowlendarCalendar(element) {
    if (!element || !(element instanceof Element) || !isVisible(element)) {
      return false;
    }

    var haystack = [
      element.id || '',
      element.className || '',
      element.getAttribute('src') || '',
      element.getAttribute('aria-label') || '',
      element.getAttribute('data-cowlendar') || ''
    ].join(' ');

    return /cowlendar|cowlender|widget\.cowlendar|booking\/embed|cow-/i.test(haystack);
  }

  function calendarIsOpen() {
    return Array.prototype.some.call(
      document.querySelectorAll('#SpjCowlendarFallbackModal.is-active, iframe, [role="dialog"], dialog, [id*="cow"], [class*="cow"], [id*="Cowlendar"], [class*="Cowlendar"]'),
      looksLikeCowlendarCalendar
    );
  }

  function trackCalendarOpened() {
    pushEvent(Object.assign({
      event: 'private_showing_calendar_opened'
    }, getLastContext()));
  }

  function checkCalendarOpenState() {
    var isOpen = calendarIsOpen();

    if (isOpen && !calendarOpenTracked) {
      calendarOpenTracked = true;
      trackCalendarOpened();
      return;
    }

    if (!isOpen) {
      calendarOpenTracked = false;
    }
  }

  function observeCalendarOpen() {
    if (!('MutationObserver' in window)) {
      return;
    }

    var observer = new MutationObserver(function () {
      checkCalendarOpenState();
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'style', 'aria-hidden', 'src'],
      childList: true,
      subtree: true
    });
  }

  function getNestedValue(payload, keys) {
    var value;

    if (!payload || typeof payload !== 'object') {
      return undefined;
    }

    keys.some(function (key) {
      if (payload[key] !== undefined && payload[key] !== null) {
        value = payload[key];
        return true;
      }

      return false;
    });

    return value;
  }

  function parseMessageData(data) {
    if (typeof data === 'string') {
      try {
        return JSON.parse(data);
      } catch (error) {
        return { message: data };
      }
    }

    if (data && typeof data === 'object') {
      return data;
    }

    return {};
  }

  function getMessageName(payload) {
    return String(getNestedValue(payload, [
      'event',
      'type',
      'action',
      'name',
      'message',
      'status'
    ]) || '').toLowerCase();
  }

  function getBookingId(payload) {
    return getNestedValue(payload, [
      'booking_id',
      'bookingId',
      'appointment_id',
      'appointmentId',
      'id'
    ]);
  }

  function isCowlendarOrigin(origin) {
    return /(^|\.)cowlendar\.com$/i.test(String(origin || '').replace(/^https?:\/\//i, ''));
  }

  function isBookingSuccessMessage(payload) {
    var name = getMessageName(payload);
    var status = String(payload.status || '').toLowerCase();
    var type = String(payload.type || payload.event || payload.action || '').toLowerCase();
    var hasBookingWord = /(booking|appointment)/i.test(name + ' ' + type);
    var hasSuccessWord = /(booked|created|confirmed|completed|\bsuccess\b)/i.test(name + ' ' + status);

    return hasBookingWord && hasSuccessWord;
  }

  function getBookedDedupeKey(context, bookingId) {
    if (bookingId) {
      return BOOKED_DEDUPE_PREFIX + ':id:' + bookingId;
    }

    return [
      BOOKED_DEDUPE_PREFIX,
      'context',
      context.source_location || 'unknown',
      context.page_type || 'unknown',
      context.product_id || 'none',
      context.variant_id || 'none',
      context.page_path || window.location.pathname
    ].join(':');
  }

  function alreadyTrackedBooking(context, bookingId) {
    var key = getBookedDedupeKey(context, bookingId);
    var now = Date.now();
    var existing = 0;

    try {
      existing = Number(window.sessionStorage.getItem(key) || 0);
    } catch (error) {
      existing = 0;
    }

    if (existing && now - existing < BOOKED_DEDUPE_TTL) {
      return true;
    }

    try {
      window.sessionStorage.setItem(key, String(now));
    } catch (error) {
      return false;
    }

    return false;
  }

  function trackBooked(payload) {
    var context = getLastContext();
    var bookingId = getBookingId(payload);

    if (alreadyTrackedBooking(context, bookingId)) {
      return;
    }

    pushEvent(Object.assign({
      event: 'private_virtual_showing_booked',
      booking_id: bookingId
    }, context));
  }

  function observeBookingMessages() {
    window.addEventListener('message', function (event) {
      if (!isCowlendarOrigin(event.origin)) {
        return;
      }

      var payload = parseMessageData(event.data);

      if (isBookingSuccessMessage(payload)) {
        trackBooked(payload);
      }
    });
  }

  function bindClickTracking() {
    document.addEventListener('click', function (event) {
      var trigger = event.target.closest(TRACKING_SELECTOR);

      if (!trigger) {
        return;
      }

      var context = getContext(trigger);
      setLastContext(context);

      pushEvent(Object.assign({
        event: 'private_showing_button_click'
      }, context));

      window.setTimeout(function () {
        checkCalendarOpenState();
      }, 1000);
    });
  }

  /*
    TODO for a fully reliable conversion signal if Cowlendar does not emit one:
    1. Enable a post-booking redirect to a Shopify thank-you/confirmation page and fire
       private_virtual_showing_booked there.
    2. Use a Cowlendar booking-created webhook to send the conversion server-side.
    3. Ask Cowlendar support for the official frontend booking success callback/event.
  */

  bindClickTracking();
  observeCalendarOpen();
  observeBookingMessages();
})();
